'use client';

import { CoreTelemetryRegistry, LogLevel, SiteBeaconTransport, SiteSessionTracker } from '@blufin/telemetry';
import { useParams, usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import { ClassifyUserAgent } from './ClassifyUserAgent/ClassifyUserAgent';
import { DeriveRoutePattern } from './DeriveRoutePattern/DeriveRoutePattern';
import { ResolveBeaconIngest } from './ResolveBeaconIngest/ResolveBeaconIngest';
import { useTelemetryAgent } from '../../hooks/useTelemetryAgent/useTelemetryAgent';

/** The apps permitted to mount the beacon — MUST match `BeaconValidator.BEACON_APP_ALLOWLIST`. */
export type SiteTelemetryApp = 'builtbybert-web' | 'honolulusoftware-web';

export interface SiteTelemetryProviderProps {
  /** The bare site name; becomes the beacon `app` label (must be in the ingest allowlist). */
  app: SiteTelemetryApp;
  children?: ReactNode;
}

/** The registry every site emits against. */
type SiteRegistry = typeof CoreTelemetryRegistry;

/** Periodic flush cadence (ms) so a low-traffic marketing session ships without waiting for tab close. */
const SITE_FLUSH_INTERVAL_MS = 10_000;

/**
 * Client provider that wires the anonymous, cookieless site beacon: a `TelemetryAgent` over
 * `SiteBeaconTransport` pointed at the env-derived ingest edge, a once-per-tab `site.session.started`
 * (carrying the coarse `user_agent_class` enum, never a raw UA) paired with an `app_session_start_total`
 * counter, an `app_page_view_total` counter on
 * every App Router route change (labelled by route *pattern*, never the raw URL — `builtbybert-web`'s
 * `[...slug]` catch-all would otherwise blow up `page` cardinality), and a `site.session.ended` flush
 * on tab close via `SiteSessionTracker`. Renders `children` unchanged; emits nothing server-side.
 * No persistent identifier is ever set — the per-tab `sessionId` lives in `sessionStorage` and dies
 * with the tab (design §7.2 / §15.2).
 * @param root0 - Component props.
 * @param root0.app - The bare site name → beacon `app` label.
 * @param root0.children - React children rendered beneath the provider.
 */
export const SiteTelemetryProvider: React.FC<SiteTelemetryProviderProps> = ({ app, children }) => {
  const pathname = usePathname();
  const params = useParams();
  const _trackerRef = useRef<SiteSessionTracker | undefined>(undefined);
  const agent = useTelemetryAgent<SiteRegistry>(
    {
      context: { app, appEnv: _resolveIngest().appEnv, appVersion: _resolveVersion() },
      /**
       * Build the browser beacon transport pointed at the env-derived ingest edge.
       * @returns The `SiteBeaconTransport` for this hostname, or `undefined` server-side (no beacon).
       */
      createTransport: () => {
        if (typeof window === 'undefined') return undefined;
        return new SiteBeaconTransport({ baseUrl: _resolveIngest().baseUrl });
      },
      /**
       * Marketing sites are low-traffic: a session may emit far fewer than the agent's size trigger,
       * so without a cadence the started + page-view batch would sit queued until `pagehide`. A 10s
       * drain ships them while the tab is open; the terminal `site.session.ended` flush handles close.
       */
      flushIntervalMs: SITE_FLUSH_INTERVAL_MS,
      /**
       * First-light per tab: stamp the per-tab session id, emit `site.session.started` with the
       * coarse UA class, and arm the `SiteSessionTracker` so `site.session.ended` flushes on tab close.
       * @param liveAgent - The freshly-created telemetry agent.
       */
      onStart: liveAgent => {
        if (typeof window === 'undefined') return;
        const tracker = new SiteSessionTracker({
          /** Wall-clock source for the foreground-duration accumulator. */
          now: () => Date.now(),
          /**
           * Terminal flush on tab close: emit `site.session.ended` and drain the queue immediately.
           * @param payload - The sealed `{ duration_seconds, pages_viewed }` from the tracker.
           */
          onSessionEnded: payload => {
            liveAgent.log({
              data: { duration_seconds: payload.duration_seconds, pages_viewed: payload.pages_viewed },
              event: 'site.session.ended',
              level: LogLevel.INFO,
              message: 'site.session.ended'
            });
            void liveAgent.flush();
          },
          sessionStorage: _safeSessionStorage(),
          visibilityDocument: document,
          window
        });
        void liveAgent.setContext({ sessionId: tracker.getSessionId() });
        liveAgent.log({
          data: {
            landing_page: window.location.pathname,
            referrer_domain: _referrerDomain(document.referrer),
            user_agent_class: ClassifyUserAgent.fromUserAgent(navigator.userAgent)
          },
          event: 'site.session.started',
          level: LogLevel.INFO,
          message: 'site.session.started'
        });
        /** Counter twin of the `site.session.started` event: the event carries the per-session detail but
         * lives in Loki (31d), while the session-count panels aggregate over Mimir's 13-month window. */
        liveAgent.counter('app_session_start_total', 1);
        _trackerRef.current = tracker;
      },
      registry: CoreTelemetryRegistry
    },
    [app]
  );

  /** Emit one `app_page_view_total{page=<route pattern>}` per App Router pathname change (§7.1). */
  useEffect(() => {
    if (!agent || pathname === null) return;
    const page = DeriveRoutePattern.fromPathname(pathname, params as Record<string, string | string[]>);
    agent.counter('app_page_view_total', 1, { page });
    _trackerRef.current?.recordPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, pathname]);

  return <>{children}</>;
};

/**
 * Extract the registrable domain of an HTTP(S) referrer, or `''` for empty/same-tab/opaque referrers.
 * @param referrer - The raw `document.referrer` value.
 */
const _referrerDomain = (referrer: string): string => {
  if (!referrer) return '';
  try {
    return new URL(referrer).hostname;
  } catch {
    return '';
  }
};

/** Resolve the ingest edge from the current hostname; SSR-safe fallback to the prod edge. */
const _resolveIngest = (): { appEnv: 'dev' | 'prod'; baseUrl: string } => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  return ResolveBeaconIngest.fromHostname(hostname);
};

/** The build-time app version, injected by the site build; `'0.0.0'` when unset. */
const _resolveVersion = (): string => {
  const version = typeof process !== 'undefined' ? process.env?.NEXT_PUBLIC_APP_VERSION : undefined;
  return version && version.length > 0 ? version : '0.0.0';
};

/**
 * Wrap `window.sessionStorage` so a throwing implementation (Safari private mode raises
 * `QuotaExceededError` on getItem/setItem) can't crash the provider — fall back to an in-memory
 * store keeping the sessionId stable for the render tree without persistence.
 */
const _safeSessionStorage = (): { getItem(key: string): string | null; setItem(key: string, value: string): void } => {
  try {
    const probe = window.sessionStorage;
    const probeKey = '__blufin_ss_probe__';
    probe.setItem(probeKey, '1');
    probe.removeItem(probeKey);
    return probe;
  } catch {
    const memory = new Map<string, string>();
    return {
      /**
       * Read a value from the in-memory fallback store.
       * @param key - The storage key.
       */
      getItem: key => memory.get(key) ?? null,
      /**
       * Write a value to the in-memory fallback store.
       * @param key - The storage key.
       * @param value - The value to store.
       */
      setItem: (key, value) => {
        memory.set(key, value);
      }
    };
  }
};
